/*
 * Gift with purchase
 *
 * Settings live on the Cart Drawer section (one "Gift tier" block per spend threshold) and are printed
 * into #gwp-config by snippets/gift-with-purchase-config.liquid, with each tier's spend amount already
 * picked for the shopper's market (in that market's currency, or in AUD with convertThreshold set, for
 * markets that sell in several currencies). This file registers Alpine.store('gwp'),
 * which watches $store.cart.cart and:
 *   - works out the qualifying spend (gift lines, gift cards and excluded tags don't count)
 *   - adds the unlocked gift (line property `_gwp` = tier id) and removes gifts that are no longer earned
 *   - builds the progress message / bar for the mini cart and cart page, and the product page gift list
 *   - shows the pop-up nudge ("spend $20 more…") and the "gift unlocked" pop-up
 *
 * The gift is made free at checkout by a Shopify automatic Buy X Get Y discount per tier — this file only
 * manages what is in the cart.
 */
(function () {
  var PROP = '_gwp';
  var DECLINED_KEY = 'gwp:declined';
  var MAX_ADDS_PER_TIER = 5;

  function readConfig() {
    var el = document.getElementById('gwp-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error(e, 'Error in reading gift with purchase config');
      return null;
    }
  }

  function readDeclined() {
    try {
      return JSON.parse(window.sessionStorage.getItem(DECLINED_KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function writeDeclined(list) {
    try {
      window.sessionStorage.setItem(DECLINED_KEY, JSON.stringify(list));
    } catch (e) {
      // Storage blocked (private mode) — a removed gift may be re-added on the next cart change
    }
  }

  function currencyRate() {
    var rate = window.Shopify && window.Shopify.currency && parseFloat(window.Shopify.currency.rate);
    return rate > 0 ? rate : 1;
  }

  // Milliseconds that timeZone is ahead of UTC at the instant ms
  function zoneOffset(ms, timeZone) {
    var parts = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date(ms))
      .forEach(function (p) {
        parts[p.type] = p.value;
      });
    var asUtc = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
    return asUtc - Math.floor(ms / 1000) * 1000;
  }

  // "YYYY-MM-DD HH:MM" (time optional) in timeZone → epoch ms; null when blank, NaN when invalid
  function parseZonedDate(value, timeZone) {
    if (!value) return null;
    var m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/.exec(value);
    if (!m) return NaN;
    var wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    // Reject dates that roll over, e.g. month 13 or 31 September
    var check = new Date(wall);
    if (check.getUTCMonth() !== +m[2] - 1 || check.getUTCDate() !== +m[3] || +(m[4] || 0) > 23 || +(m[5] || 0) > 59) {
      return NaN;
    }
    // Apply the offset twice so a date on the other side of a daylight saving change settles on the right one
    var guess = wall - zoneOffset(wall, timeZone);
    return wall - zoneOffset(guess, timeZone);
  }

  // A date that can't be read is listed in `invalid` (and keeps the promotion off) rather than ignored,
  // so a typo never starts it early or makes it run forever
  function scheduleBounds(schedule) {
    var s = schedule || {};
    var bounds = { start: parseZonedDate(s.start, s.timeZone), end: parseZonedDate(s.end, s.timeZone), invalid: [] };
    ['start', 'end'].forEach(function (key) {
      if (bounds[key] !== bounds[key]) {
        bounds.invalid.push(key);
        bounds[key] = null;
      }
    });
    return bounds;
  }

  function formatAmount(n) {
    var rounded = Math.round(n * 100) / 100;
    return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
  }

  // Gift lines carry the tier id in line property _gwp; the cart data view exposes it as item.gwp_tier
  function giftProperties(tierId) {
    var props = {};
    props[PROP] = tierId;
    return props;
  }

  function isGiftLine(item) {
    return !!item.gwp_tier;
  }

  function firstAvailableVariant(tier) {
    var variants = (tier.product && tier.product.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].available) return variants[i];
    }
    return null;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('Gift with purchase cart request failed: ' + res.status);
      return res.json();
    });
  }

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    return {
      add: r.cart_add_url || '/cart/add.js',
      change: (r.cart_change_url || '/cart/change') + '.js',
    };
  }

  function createStore(cfg) {
    return {
      cfg: cfg,
      active: false,
      tiers: [],
      markers: [],
      message: '',
      progress: 0,
      subtotal: 0,
      openTierId: null,
      claimingId: null,
      scheduleError: '',
      pdpTiers: [],
      toast: { show: false, type: '', heading: '', message: '', image: '', imageAlt: '' },

      _busy: false,
      _hasPending: false,
      _prevSubtotal: null,
      _prevUnlocked: [],
      _prevGiftTiers: null,
      _expectRemoved: {},
      _failed: {},
      _adds: {},
      _toastTimer: null,
      _scheduleTimer: null,

      money: function (amount) {
        return this.cfg.moneySymbol + formatAmount(amount);
      },

      fill: function (template, tier, amountLeft) {
        return (template || '')
          .replace(/\{\{\s*amount_left\s*\}\}/g, this.money(Math.max(0, amountLeft || 0)))
          .replace(/\{\{\s*gift\s*\}\}/g, tier ? tier.name : '')
          .replace(/\{\{\s*threshold\s*\}\}/g, tier ? this.money(tier.min) : '');
      },

      sync: function (cart) {
        if (!cart) return;
        if (this._busy) {
          this._hasPending = true;
          return;
        }

        var self = this;
        var cfg = this.cfg;
        var rate = currencyRate();
        // Tiers without a spend amount for this market are left out of the config, so no tiers = not offered here
        var allowed = cfg.enabled && this.isScheduled();
        var excludeTags = cfg.excludeTags;

        var subtotal = 0;
        var giftByTier = {};
        var giftLines = [];
        (cart.items || []).forEach(function (item) {
          if (isGiftLine(item)) {
            giftLines.push(item);
            (giftByTier[item.gwp_tier] = giftByTier[item.gwp_tier] || []).push(item);
            return;
          }
          if (item.gift_card) return;
          var tags = (item.product_tags || []).map(function (t) {
            return String(t).toLowerCase();
          });
          for (var i = 0; i < excludeTags.length; i++) {
            if (tags.indexOf(excludeTags[i]) > -1) return;
          }
          subtotal += (item.final_line_price_unformatted || 0) / 100;
        });
        subtotal = Math.round(subtotal * 100) / 100;

        var tiers = cfg.tiers
          .map(function (t) {
            var min = cfg.convertThreshold ? Math.round(t.threshold * rate * 100) / 100 : t.threshold;
            return Object.assign({}, t, { min: min });
          })
          .sort(function (a, b) {
            return a.min - b.min;
          });

        var unlocked = allowed
          ? tiers.filter(function (t) {
              return subtotal >= t.min;
            })
          : [];
        var target = cfg.mode === 'stack' ? unlocked : unlocked.slice(-1);
        var targetIds = {};
        target.forEach(function (t) {
          targetIds[t.id] = true;
        });
        var unlockedIds = unlocked.map(function (t) {
          return t.id;
        });

        // A gift the customer removed themselves stays out of the cart until they claim it again.
        // Declines are forgotten once the tier is no longer unlocked.
        var declined = readDeclined().filter(function (id) {
          return targetIds[id];
        });
        if (this._prevGiftTiers) {
          this._prevGiftTiers.forEach(function (id) {
            if (!giftByTier[id] && !self._expectRemoved[id] && targetIds[id] && declined.indexOf(id) === -1) {
              declined.push(id);
            }
          });
        }
        this._expectRemoved = {};
        writeDeclined(declined);

        var actions = [];
        Object.keys(giftByTier).forEach(function (id) {
          giftByTier[id].forEach(function (line, index) {
            if (!targetIds[id] || index > 0) {
              actions.push({ type: 'change', key: line.key, quantity: 0, tier: id });
            } else if (line.quantity > 1) {
              actions.push({ type: 'change', key: line.key, quantity: 1, tier: id });
            }
          });
        });
        target.forEach(function (t) {
          if (giftByTier[t.id] || declined.indexOf(t.id) > -1 || t.addMode !== 'auto' || self._failed[t.id]) return;
          var variant = firstAvailableVariant(t);
          if (variant) actions.push({ type: 'add', tier: t.id, variantId: variant.id });
        });

        var next = allowed
          ? tiers.find(function (t) {
              return subtotal < t.min;
            })
          : null;
        var amountLeft = next ? next.min - subtotal : 0;
        var top = unlocked[unlocked.length - 1];
        var max = tiers.length ? tiers[tiers.length - 1].min : 0;

        this.subtotal = subtotal;
        this.active = allowed && tiers.length > 0;
        this.tiers = tiers.map(function (t) {
          var inCart = !!giftByTier[t.id];
          var isTarget = !!targetIds[t.id];
          var soldOut = !firstAvailableVariant(t);
          var status;
          if (inCart && isTarget) status = cfg.text.added;
          else if (isTarget) status = soldOut ? cfg.text.soldOut : cfg.text.unlocked;
          else if (subtotal >= t.min && allowed) status = cfg.text.upgraded;
          else status = self.fill(cfg.text.locked, t, t.min - subtotal);
          return Object.assign({}, t, {
            inCart: inCart && isTarget,
            unlocked: subtotal >= t.min && allowed,
            superseded: subtotal >= t.min && allowed && !isTarget,
            soldOut: soldOut,
            canClaim: isTarget && !inCart && !soldOut,
            status: status,
          });
        });

        if (!this.active) {
          this.message = '';
          this.progress = 0;
          this.markers = [];
        } else if (cfg.progressStyle === 'next' && next) {
          var from = top ? top.min : 0;
          this.progress = Math.max(0, Math.min(100, ((subtotal - from) / (next.min - from)) * 100));
          this.markers = [];
        } else {
          this.progress = max > 0 ? Math.max(0, Math.min(100, (subtotal / max) * 100)) : 0;
          this.markers =
            cfg.progressStyle === 'next'
              ? []
              : tiers.slice(0, -1).map(function (t) {
                  return { id: t.id, pos: (t.min / max) * 100, done: subtotal >= t.min };
                });
        }
        if (this.active) {
          this.message = next
            ? this.fill(next.progressMessage || cfg.text.progress, next, amountLeft)
            : this.fill(top.completeMessage || cfg.text.complete, top, 0);
        }

        // Product page: every gift with its own status — "spend $200" with nothing in the bag,
        // "spend $X more" once it's on its way, "in your bag" once added
        var pdp = cfg.pdp || {};
        this.pdpTiers = !this.active
          ? []
          : this.tiers.map(function (t) {
              var status;
              if (t.inCart) status = pdp.statusAdded;
              else if (t.superseded) status = cfg.text.upgraded;
              else if (t.unlocked) status = t.soldOut ? cfg.text.soldOut : cfg.text.unlocked;
              else status = self.fill(subtotal > 0 ? pdp.statusProgress : pdp.statusLocked, t, t.min - subtotal);
              return {
                id: t.id,
                name: t.name,
                image: t.image,
                imageLarge: t.imageLarge,
                description: t.description,
                inCart: t.inCart,
                status: status,
              };
            });

        // Pop-ups only react to changes made on this page, not to the cart as it was on load.
        if (this.active && this._prevSubtotal !== null && subtotal !== this._prevSubtotal && subtotal > 0) {
          var newlyUnlocked = target.filter(function (t) {
            return self._prevUnlocked.indexOf(t.id) === -1;
          });
          var within = cfg.popup.within * rate;
          if (newlyUnlocked.length && cfg.popup.onUnlock) {
            this.showToast('unlock', newlyUnlocked[newlyUnlocked.length - 1], 0);
          } else if (next && cfg.popup.enabled && (cfg.popup.within <= 0 || amountLeft <= within)) {
            this.showToast('nudge', next, amountLeft);
          }
        }

        this._prevSubtotal = subtotal;
        this._prevUnlocked = unlockedIds;
        this._prevGiftTiers = Object.keys(giftByTier).filter(function (id) {
          return targetIds[id];
        });

        if (actions.length) this.run(actions);
      },

      run: function (actions) {
        var self = this;
        var r = routes();
        this._busy = true;

        actions.forEach(function (a) {
          if (a.type === 'change' && a.quantity === 0) self._expectRemoved[a.tier] = true;
          // Safety net: never keep re-adding the same gift if the cart doesn't reflect it
          if (a.type === 'add') {
            self._adds[a.tier] = (self._adds[a.tier] || 0) + 1;
            if (self._adds[a.tier] > MAX_ADDS_PER_TIER) self._failed[a.tier] = true;
          }
        });
        actions = actions.filter(function (a) {
          return !(a.type === 'add' && self._failed[a.tier]);
        });

        var chain = Promise.resolve();
        actions.forEach(function (a) {
          chain = chain.then(function () {
            var req =
              a.type === 'add'
                ? post(r.add, { items: [{ id: a.variantId, quantity: 1, properties: giftProperties(a.tier) }] })
                : post(r.change, { id: a.key, quantity: a.quantity });
            return req.catch(function (e) {
              console.error(e, 'Error in updating gift with purchase');
              if (a.type === 'add') self._failed[a.tier] = true;
            });
          });
        });

        return chain.then(function () {
          self._busy = false;
          self._hasPending = false;
          return window.Alpine.store('cart').fetchCart({ showLoading: false });
        });
      },

      claim: function (tierId, variantId) {
        var tier = this.tiers.find(function (t) {
          return t.id === tierId;
        });
        if (!tier || this.claimingId) return;
        var variant = variantId ? { id: variantId } : firstAvailableVariant(tier);
        if (!variant) return;

        var self = this;
        writeDeclined(
          readDeclined().filter(function (id) {
            return id !== tierId;
          })
        );
        delete this._failed[tierId];
        delete this._adds[tierId];
        this.claimingId = tierId;
        this.run([{ type: 'add', tier: tierId, variantId: variant.id }]).finally(function () {
          self.claimingId = null;
          self.openTierId = null;
        });
      },

      pick: function (tierId) {
        var tier = this.tiers.find(function (t) {
          return t.id === tierId;
        });
        if (!tier) return;
        if (tier.product.has_only_default_variant) return this.claim(tierId);
        this.openTierId = this.openTierId === tierId ? null : tierId;
      },

      // Inside the start/end dates. The theme editor ignores them so the promotion can be set up in advance.
      isScheduled: function () {
        var bounds = scheduleBounds(this.cfg.schedule);
        this.scheduleError = bounds.invalid.length
          ? 'Gift with purchase is off: the ' + bounds.invalid.join(' and ') + ' date must be written as YYYY-MM-DD HH:MM'
          : '';
        if (window.Shopify && window.Shopify.designMode) return true;
        if (bounds.invalid.length) {
          console.warn(this.scheduleError);
          return false;
        }
        var now = Date.now();
        return (bounds.start === null || now >= bounds.start) && (bounds.end === null || now < bounds.end);
      },

      // Re-sync when the promotion starts or ends while the page is open
      watchSchedule: function () {
        var self = this;
        clearTimeout(this._scheduleTimer);
        var bounds = scheduleBounds(this.cfg.schedule);
        var now = Date.now();
        var next = [bounds.start, bounds.end]
          .filter(function (t) {
            return t !== null && t > now;
          })
          .sort(function (a, b) {
            return a - b;
          })[0];
        // setTimeout can't wait longer than ~24.8 days
        if (next === undefined || next - now > 2147483647) return;
        this._scheduleTimer = setTimeout(function () {
          self.sync(window.Alpine.raw(window.Alpine.store('cart').cart));
          self.watchSchedule();
        }, next - now + 1000);
      },

      showToast: function (type, tier, amountLeft) {
        var cfg = this.cfg;
        var cartStore = window.Alpine.store('cart');
        if (cfg.popup.hideWhenCartOpen && cartStore && cartStore.isMiniCartOpen) return;

        var self = this;
        var isUnlock = type === 'unlock';
        this.toast.type = type;
        this.toast.heading = this.fill(isUnlock ? cfg.popup.unlockHeading : cfg.popup.nudgeHeading, tier, amountLeft);
        this.toast.message = this.fill(
          isUnlock
            ? tier.addMode === 'auto'
              ? cfg.popup.unlockMessage
              : cfg.popup.unlockChooseMessage
            : cfg.popup.nudgeMessage,
          tier,
          amountLeft
        );
        this.toast.image = cfg.popup.showImage ? tier.image : '';
        this.toast.imageAlt = tier.name;
        this.toast.show = true;

        clearTimeout(this._toastTimer);
        if (cfg.popup.duration > 0) {
          this._toastTimer = setTimeout(function () {
            self.toast.show = false;
          }, cfg.popup.duration * 1000);
        }
      },

      hideToast: function () {
        clearTimeout(this._toastTimer);
        this.toast.show = false;
      },

      openCart: function () {
        this.hideToast();
        window.Alpine.store('cart').toggleMiniCart(true);
      },

      previewToast: function () {
        var cfg = this.cfg;
        var mode = cfg.popup.preview;
        if (!(window.Shopify && window.Shopify.designMode) || !mode || mode === 'off' || !cfg.tiers.length) {
          return this.hideToast();
        }
        var tier = Object.assign({}, cfg.tiers[0], { min: cfg.tiers[0].threshold });
        this.showToast(mode === 'unlock' ? 'unlock' : 'nudge', tier, Math.min(20, tier.min));
        clearTimeout(this._toastTimer);
      },
    };
  }

  // Product page gift quick view: small panel over a sheer background, same pattern as the Complete the
  // look pop-up (blocks/_product-shop-the-look.liquid) — one gift at a time, no Add to bag
  function giftPopup() {
    return {
      activeIndex: null,
      touchX: null,

      count: function () {
        return this.$store.gwp.pdpTiers.length;
      },

      open: function (i) {
        var self = this;
        this.activeIndex = Math.max(0, Math.min(i, this.count() - 1));
        document.documentElement.classList.add('gwp-gift-modal-open');
        this.$nextTick(function () {
          if (self.$refs.dialog) self.$refs.dialog.focus();
        });
      },

      close: function () {
        if (this.activeIndex === null) return;
        this.activeIndex = null;
        document.documentElement.classList.remove('gwp-gift-modal-open');
      },

      go: function (step) {
        var n = this.count();
        if (n < 2 || this.activeIndex === null) return;
        this.activeIndex = (this.activeIndex + step + n) % n;
      },

      swipe: function (event) {
        if (this.touchX === null) return;
        var dx = event.changedTouches[0].clientX - this.touchX;
        this.touchX = null;
        if (Math.abs(dx) > 40) this.go(dx < 0 ? 1 : -1);
      },
    };
  }

  document.addEventListener('alpine:init', function () {
    var cfg = readConfig() || { enabled: false, tiers: [], excludeTags: [], text: {}, popup: {} };
    window.Alpine.store('gwp', createStore(cfg));
    window.Alpine.data('GwpGiftPopup', giftPopup);
  });

  document.addEventListener('alpine:initialized', function () {
    var Alpine = window.Alpine;
    var store = Alpine.store('gwp');
    if (!Alpine.store('cart')) return;

    Alpine.effect(function () {
      var cart = Alpine.store('cart').cart;
      // Run outside the effect so the store's own writes don't become dependencies of it
      setTimeout(function () {
        store.sync(Alpine.raw(cart));
      });
    });

    store.previewToast();
    store.watchSchedule();

    // Theme editor: pick up setting changes without a full reload
    document.addEventListener('shopify:section:load', function () {
      var cfg = readConfig();
      if (!cfg) return;
      store.cfg = cfg;
      store._failed = {};
      store._adds = {};
      store.sync(Alpine.raw(Alpine.store('cart').cart));
      store.previewToast();
      store.watchSchedule();
    });
  });
})();
